import { Divider, Grid, Menu, MenuItem, Typography } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import { FeedClassKey } from "assets/jss/common-styles";
import ButtonCardAction from "components/Buttons/ButtonCardAction";
import ButtonEllipsis from "components/Buttons/ButtonEllipsis";
import ButtonFollow from "components/Buttons/ButtonFollow";
import IconButtonEdit from "components/Buttons/IconButtonEdit";
import IconClipboardCheck from "components/Icons/IconClipboardCheck";
import IconCourses from "components/Icons/IconCourses";
import IconEditSquare from "components/Icons/IconEditSquare";
import IconFollowers from "components/Icons/IconFollowers";
import IconKnowsAbout from "components/Icons/IconKnowsAbout";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiImage from "components/MuiExtras/MuiImage";
import MuiPaper from "components/MuiExtras/MuiPaper";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import HtmlUtils from "lib/HtmlUtils";
import { SystemRoles } from "models/ApiEnums";
import { TopicListAdminItemDto, TopicUserFlowListItemDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router";
import { RoutePaths } from "routes/RouteConfig";
import Session from "services/Session";
import { KnowledgeTestAdminListParameters } from "views/KnowledgeTest/KnowledgeTestAdminList";
import { PublicationAdminListParameters } from "views/Publication/PublicationAdminList";
import TopicFollowersDrawer from "views/Topic/Dialogs/TopicFollowersDrawer";
import TopicWhoKnowsAboutDrawer from "views/Topic/Dialogs/TopicWhoKnowsAboutDrawer";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    menuShareAnchorEl: any;
    menuAnchorEl: any;

    mouseOver: boolean;
    dialog: any;
}

interface IProps {
    topic: TopicListAdminItemDto;
    onEdit?: () => void;
    onJoin?: () => void;
    onDelete?: () => void;
    onFollow?: () => void;
    onViewFeed?: () => void;
    onViewHierarchy?: () => void;
    onRelationships?: () => void;
}

type Props = IProps & WithSnackbar & StyledComponentProps<FeedClassKey> & RouteComponentProps & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class TopicCard extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null,
            mouseOver: false,
            menuAnchorEl: null,
            menuShareAnchorEl: null
        };
    }

    ///
    private handleEdit = () => {
        this.handleCloseMenu();
        this.props.onEdit();
    };

    ///
    private handleDelete = () => {
        this.handleCloseMenu();
        this.props.onDelete();

    };

    ///
    private handleJoin = () => {
        this.handleCloseMenu();
        this.props.onJoin();
    };

    ///
    private handleRelationships = () => {
        this.handleCloseMenu();
        this.props.onRelationships();
    };

    ///
    private handleViewDiagram= () => {
        this.handleCloseMenu();
        this.props.onViewHierarchy();
    };

    ///
    private handleView = () => {
        this.handleCloseMenu();
        this.props.onViewFeed();
    };

    ///
    private handleCloseMenu() {
        this.setState({ menuAnchorEl: null, mouseOver: false });
    }

    ///
    private buildOverflowMenu(): Array<any> {

        const actionMenu: Array<any> = [];

        if (this.props.onViewFeed) actionMenu.push(<MenuItem key="view" onClick={this.handleView}>{this.props.t("Views.Topic.Common.Actions.View")}</MenuItem>);

        if (actionMenu.length > 0) actionMenu.push(<Divider key="div1" />);
        if (this.props.onEdit) actionMenu.push(<MenuItem key="edit" onClick={this.handleEdit}>{this.props.t("Views.Topic.Common.Actions.Edit")}</MenuItem>);
        if (this.props.onJoin) actionMenu.push(<MenuItem key="join" onClick={this.handleJoin}>{this.props.t("Views.Topic.Common.Actions.Join")}</MenuItem>);

        if (actionMenu.length > 0) actionMenu.push(<Divider key="div2" />);
        if (this.props.onRelationships) actionMenu.push(<MenuItem key="relationships" onClick={this.handleRelationships}>{this.props.t("Views.Topic.Common.Actions.Relationships") + "..."}</MenuItem>);

        if (this.props.topic.hasDescendents && this.props.onViewHierarchy) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="div3" />);
            if (this.props.onViewHierarchy) actionMenu.push(<MenuItem key="relationships" onClick={this.handleViewDiagram}>{this.props.t("Views.Topic.Common.Actions.ViewHierarchy") + "..."}</MenuItem>);
        }

        if (this.props.onDelete) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="div4" />);
            actionMenu.push(<MenuItem key="delete" onClick={this.handleDelete}><MuiColor variant="error">{this.props.t("Views.Topic.Common.Actions.Delete")}</MuiColor></MenuItem>);
        }

        return actionMenu;
    }

    ///
    private handleFollower = () => {
        this.setState({
            dialog: <TopicFollowersDrawer
                topicId={this.props.topic.topicId}
                onClose={() => { this.setState({ dialog: null }); }}
            />
        });
    };

    ///
    private handleWhoKnowsAbout = () => {
        this.setState({
            dialog: <TopicWhoKnowsAboutDrawer
                topicId={this.props.topic.topicId}
                onClose={() => { this.setState({ dialog: null }); }}
            />
        });
    };

    ///
    private handleCourse = () => {
        const { topicId, name, thumbnailUrl } = this.props.topic;

        this.props.history.push(RoutePaths.CourseAdminList, {
            topic:
            {
                key: topicId,
                label: name,
                thumbnailUrl: thumbnailUrl,
                canDelete: true,
            }
        } as PublicationAdminListParameters);
    };

    ///
    private handlePublications = () => {
        const { topicId, name, thumbnailUrl } = this.props.topic;

        if (Session.hasRole(SystemRoles.PublicationManager)) {
            this.props.history.push(RoutePaths.PublicationAdminList, {
                topic:
                {
                    key: topicId,
                    label: name,
                    thumbnailUrl: thumbnailUrl,
                    canDelete: true,
                }
            } as PublicationAdminListParameters);
        }
        else {
            HtmlUtils.openUrlInNewWindow(RoutePaths.TopicViewer.replace(":id", topicId));
        }
    };

    ///
    private handleKnowledgeTests = () => {
        const { topicId, name, thumbnailUrl } = this.props.topic;

        this.props.history.push(RoutePaths.KnowledgeTestAdminList, {
            topic:
            {
                key: topicId,
                label: name,
                thumbnailUrl: thumbnailUrl,
                canDelete: true,
            }
        } as KnowledgeTestAdminListParameters);
    };

    ///
    public render() {
        const {
            classes,
            topic
        } = this.props;

        const { mouseOver } = this.state;

        const overflowActions = this.buildOverflowMenu();

        const topicName = <Typography variant="h6">{topic.name}</Typography>;

        const thumbnail = <MuiImage size="sm" orientation="square" src={topic.thumbnailUrl} />;

        return (
            <>
                <MuiPaper className={classes.card}
                    onMouseEnter={() => this.setState({ mouseOver: true })}
                    onMouseLeave={() => this.setState({ mouseOver: false })
                    }
                >
                    <Grid container spacing={2} >
                        <Grid item>
                            {
                                this.props.onViewFeed
                                    ? <MuiClickable onClick={this.props.onViewFeed}>{thumbnail}</MuiClickable>
                                    : thumbnail
                            }

                        </Grid>
                        <Grid item xs>

                            <Grid container justifyContent="space-between" alignItems="flex-start" wrap="nowrap">
                                <Grid item>
                                    {
                                        this.props.onEdit
                                            ? <MuiClickable onClick={this.props.onEdit}>{topicName}</MuiClickable>
                                            : topicName
                                    }
                                </Grid>
                                {
                                    this.props.onEdit &&

                                    <Grid item>
                                        <IconButtonEdit
                                            size="small"
                                            visible={mouseOver}
                                            onClick={this.handleEdit}
                                        />
                                    </Grid>
                                }
                            </Grid>


                            <Grid container
                                justifyContent="space-between"
                                alignItems="center"
                                className={this.props.classes.cardActions}
                            >

                                <Grid item>
                                    <Grid container alignItems="center">
                                        <Grid item>
                                            <ButtonCardAction
                                                title={this.props.t("Common.Feed.Actions.ButtonFollowersTitle")}
                                                onClick={this.handleFollower}
                                                startIcon={<IconFollowers />}
                                            >
                                                <span>{topic.followers}</span>
                                            </ButtonCardAction>

                                            <ButtonCardAction
                                                title={this.props.t("Common.Feed.Actions.ButtonPeopleKnowsTopicTitle")}
                                                onClick={this.handleWhoKnowsAbout}
                                                startIcon={<IconKnowsAbout />}
                                            >
                                                <span>{topic.whoKnowsAbout}</span>
                                            </ButtonCardAction>

                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item>
                                    <ButtonCardAction
                                        title={this.props.t("Common.Feed.Actions.ButtonPublicationsTitle")}
                                        onClick={this.handlePublications}
                                        startIcon={<IconEditSquare />}
                                    >
                                        <span>{topic.publications}</span>
                                    </ButtonCardAction>

                                    <ButtonCardAction
                                        title={this.props.t("Common.Feed.Actions.ButtonCoursesTitle")}
                                        onClick={this.handleCourse}
                                        startIcon={<IconCourses />}
                                        noPress={!Session.hasRole(SystemRoles.CourseCreator || SystemRoles.CourseManager)}
                                    >
                                        <span>{topic.courses}</span>
                                    </ButtonCardAction>

                                    <ButtonCardAction
                                        title={this.props.t("Common.Feed.Actions.ButtonKnowledgeTestsTitle")}
                                        onClick={this.handleKnowledgeTests}
                                        startIcon={<IconClipboardCheck />}
                                        noPress={!Session.hasRole(SystemRoles.TestCreator || SystemRoles.TrainingManager)}
                                    >
                                        <span>{topic.knowledgeTests}</span>
                                    </ButtonCardAction>
                                </Grid>

                                <Grid item>
                                    <Grid container alignItems="center">

                                        {
                                            this.props.onFollow && (topic as TopicUserFlowListItemDto).following != undefined &&
                                            <Grid item>
                                                <ButtonFollow
                                                    following={(topic as TopicUserFlowListItemDto).following}
                                                    followColor={"success"}
                                                    unfollowColor={"warning"}
                                                    onClick={this.props.onFollow}
                                                />
                                            </Grid>
                                        }


                                        {
                                            overflowActions.length > 0 &&
                                            <Grid item>
                                                <ButtonEllipsis
                                                    onClick={
                                                        (e) => {
                                                            this.setState({ menuAnchorEl: e.currentTarget });
                                                        }
                                                    }
                                                />
                                                <Menu
                                                    open={this.state.menuAnchorEl != null}
                                                    anchorEl={this.state.menuAnchorEl}
                                                    onClose={() => { this.handleCloseMenu(); }}
                                                >
                                                    {overflowActions}
                                                </Menu>
                                            </Grid>
                                        }
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Grid>
                    </Grid>

                </MuiPaper>


                {this.state.dialog}
            </>
        );
    }
}

export default withRouter(withSnackbar(withTranslation()(TopicCard)));